import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { fmtBRL, fmtTime } from "@/lib/api";
import { CheckCircle2, RotateCcw, ChevronDown, ChevronUp, FileText, MessageSquare, Ban, Phone, Loader2, Store, PackageCheck } from "lucide-react";
import { StatusBadge, pickCardBorder, formatCellPhone, buildPedidoLabel } from "./mobileHelpers";
import { WaitingTimer, pickWaitSince } from "@/components/WaitingTimer";
import { pedidoProgress } from "./pedidoMatch";

/**
 * Cartão de uma entrega individual no MobileEntregador.
 * Props: it, lookup, formas, unread, expanded, onToggle, onOpenChat, onChangeFormaPgto, onSetStatus,
 *        onConfirmarRecebimento
 *        flowBusy = { status: 'concluida|remarcar|cancelada', phase: 'pending'|'success' } | null
 *          → controla spinner/check nos botões de finalização (feedback UX fev/2026)
 * fev/2026 (v5): quando status="aguardando_conferencia", o card exibe SÓ o botão
 *   grande "Confirmar Recebimento" (não os botões de finalização). O entregador
 *   precisa reconhecer a posse da mercadoria antes de rodar o fluxo normal.
 */
export function MobileEntregaCard({ it, lookup, formas, unread, expanded, flowBusy, onToggle, onOpenChat, onChangeFormaPgto, onSetStatus, onConfirmarRecebimento }) {
  const isCollapsed = expanded === undefined
    ? it.status === "concluida" // concluídas iniciam colapsadas
    : !expanded;
  const isPriority = it.prioridade === "urgente" || it.prioridade === "vip";
  const borderColor = pickCardBorder(it);
  const telDigits = (it.cliente_telefone || "").replace(/\D/g, "");
  const telFormatted = formatCellPhone(it.cliente_telefone);
  // Feedback do fluxo de finalização — botão que iniciou o fluxo mostra spinner
  // "Enviando…" (phase=pending) ou CheckCircle verde "Enviado!" (phase=success).
  // Demais botões do card ficam desabilitados até o fluxo terminar.
  const isBusy = !!flowBusy;
  const busyStatus = flowBusy?.status;
  // Progresso de conferência por-pedido (v6 fev/2026) — só relevante quando a
  // entrega tem múltiplos pedidos preenchidos e ainda está em aguardando_conferencia.
  const confProgress = it.status === "aguardando_conferencia" ? pedidoProgress(it) : null;
  const showConfProgress = !!confProgress && confProgress.total > 1;
  const busyPhase = flowBusy?.phase;
  const renderActionBtnContent = (status, DefaultIcon, defaultText) => {
    if (busyStatus === status && busyPhase === "pending") {
      return (<><Loader2 className="w-4 h-4 mr-1 animate-spin" />Enviando…</>);
    }
    if (busyStatus === status && busyPhase === "success") {
      return (<><CheckCircle2 className="w-4 h-4 mr-1" />Enviado!</>);
    }
    return (<><DefaultIcon className="w-4 h-4 mr-1" />{defaultText}</>);
  };
  const btnSuccessCls = "bg-green-600 hover:bg-green-700 ring-2 ring-green-300";
  return (
    <div className={`bg-white rounded-2xl shadow-md p-4 border-l-4 ${borderColor} ${isPriority && it.status !== "concluida" ? "ring-2 ring-red-100" : ""}`} data-testid={`mobile-entrega-${it.id}`} data-mobile-card-id={it.id}>
      <button type="button" onClick={onToggle} className="w-full flex justify-between items-start mb-2 text-left" data-testid={`mobile-toggle-${it.id}`}>
        <div className="min-w-0 flex-1">
          <div className="text-[10px] text-muted-foreground uppercase tracking-wide" data-testid={`mobile-pedido-${it.id}`}>
            {fmtTime(it.hr_saida)} · {buildPedidoLabel(it, lookup.tip[it.tipo_id])}
            {Array.isArray(it.pedidos_multiplos) && it.pedidos_multiplos.filter(Boolean).length >= 2 && (
              <span className="ml-1 inline-flex items-center gap-0.5 rounded-full bg-blue-600 text-white text-[9px] font-bold px-1.5 py-0.5" data-testid={`mobile-multi-badge-${it.id}`}>
                1 entrega · {it.pedidos_multiplos.filter(Boolean).length} pedidos
              </span>
            )}
            {it.empresa_nome && (
              <span className="ml-1 inline-flex items-center gap-0.5 rounded-full bg-orange-100 text-orange-800 border border-orange-200 text-[9px] font-bold px-1.5 py-0.5" data-testid={`mobile-empresa-badge-${it.id}`}>
                <Store className="w-2.5 h-2.5" />{it.empresa_nome}
              </span>
            )}
            {(it.tipo_nome || lookup.tip[it.tipo_id]) && (
              <span className="ml-1 inline-flex items-center rounded-full bg-indigo-100 text-indigo-800 border border-indigo-200 text-[9px] font-bold px-1.5 py-0.5 uppercase tracking-wide" data-testid={`mobile-tipo-badge-${it.id}`}>
                {it.tipo_nome || lookup.tip[it.tipo_id]}
              </span>
            )}
          </div>
          <div className="text-lg font-extrabold truncate leading-tight" data-testid={`mobile-cliente-nome-${it.id}`}>
            {it.cliente_nome || <span className="text-muted-foreground italic font-normal">Sem nome do cliente</span>}
          </div>
          <div className="text-sm text-neutral-700 truncate" data-testid={`mobile-bairro-${it.id}`}>
            📍 {it.bairro_nome || lookup.bai[it.bairro_id] || "Bairro não informado"}
            {(it.regiao_nome || lookup.reg[it.regiao_id]) ? <span className="text-muted-foreground"> · {it.regiao_nome || lookup.reg[it.regiao_id]}</span> : null}
          </div>
        </div>
        <div className="flex flex-col items-end gap-1 shrink-0 ml-2">
          {it.prioridade === "urgente" && (
            <span className="text-[10px] font-extrabold uppercase bg-red-600 text-white px-2 py-0.5 rounded-full animate-pulse" data-testid={`mobile-badge-urgente-${it.id}`}>🚨 Urgente</span>
          )}
          {it.prioridade === "vip" && (
            <span className="text-[10px] font-extrabold uppercase bg-purple-600 text-white px-2 py-0.5 rounded-full" data-testid={`mobile-badge-vip-${it.id}`}>⭐ VIP</span>
          )}
          <StatusBadge s={it.status} />
          {it.status === "aguardando_conferencia" && (
            <WaitingTimer
              sinceIso={pickWaitSince(it)}
              size="sm"
              testId={`mobile-wait-timer-${it.id}`}
            />
          )}
          {showConfProgress && (
            <span
              className={`text-[10px] font-extrabold uppercase px-2 py-0.5 rounded-full flex items-center gap-1 ${
                confProgress.done === 0
                  ? "bg-slate-200 text-slate-700"
                  : confProgress.all_done
                    ? "bg-emerald-600 text-white"
                    : "bg-sky-600 text-white"
              }`}
              data-testid={`mobile-conf-progress-${it.id}`}
              data-done={confProgress.done}
              data-total={confProgress.total}
              title={`Pedidos escaneados: ${confProgress.done} de ${confProgress.total}`}
            >
              <PackageCheck className="w-3 h-3" />
              {confProgress.done}/{confProgress.total} pedidos
            </span>
          )}
          {isCollapsed ? <ChevronDown className="w-4 h-4 text-muted-foreground" /> : <ChevronUp className="w-4 h-4 text-muted-foreground" />}
        </div>
      </button>

      {telDigits && (
        <a href={`tel:+55${telDigits}`}
          onClick={(e) => e.stopPropagation()}
          className="flex items-center gap-2 mb-2 py-1.5 px-2 rounded-md bg-blue-50 hover:bg-blue-100 text-blue-800 border border-blue-200 text-sm font-semibold"
          data-testid={`mobile-tel-${it.id}`}>
          <Phone className="w-4 h-4" />
          <span className="mono">{telFormatted}</span>
          <span className="text-xs text-blue-600 ml-auto">Toque para ligar</span>
        </a>
      )}

      {isCollapsed && it.valor > 0 && (
        <div className="text-sm mb-2 text-orange-700 font-bold">{fmtBRL(it.valor)}</div>
      )}

      {!isCollapsed && (
        <>
          {it.buscar_receita && (
            <div className="mb-3 bg-emerald-50 border-2 border-emerald-500 rounded-lg p-3 flex items-center gap-2" data-testid={`mobile-receita-${it.id}`}>
              <FileText className="w-6 h-6 text-emerald-700 shrink-0" />
              <div>
                <div className="text-sm font-extrabold text-emerald-900 uppercase">Trazer receita!</div>
                <div className="text-xs text-emerald-800">Não esqueça de pegar a receita/prescrição com o cliente.</div>
              </div>
            </div>
          )}
          <div className="grid grid-cols-2 gap-2 text-sm mb-3">
            <div><span className="text-muted-foreground text-xs">Valor</span><div className="font-bold text-orange-700">{fmtBRL(it.valor)}</div></div>
            <div>
              <span className="text-muted-foreground text-xs">Pagamento</span>
              {/*
                Radix Portal do Select DEVE permanecer montado mesmo quando o status
                muda pra "concluida". Se removermos o <Select> condicionalmente,
                seu Portal é desmontado no meio de animações de outros Dialogs,
                causando "Failed to execute insertBefore/removeChild on Node".
                Solução: sempre renderiza o Select, mas com `disabled` quando concluida.

                Multi-empresa (fev/2026): `formas` recebido do MobileEntregador é
                escopado pela empresa DA ENTREGA (via `formasByEmpresa[it.empresa_id]`),
                então o Select sempre encontra o `it.forma_pgto_id` e o Radix consegue
                renderizar o nome. Editável em qualquer loja do user.
              */}
              <Select
                value={it.forma_pgto_id || "__none__"}
                onValueChange={(v) => onChangeFormaPgto(it, v === "__none__" ? null : v)}
                disabled={it.status === "concluida"}
              >
                <SelectTrigger className="h-8 text-sm" data-testid={`mobile-forma-${it.id}`} data-pagamento={it.forma_pgto_nome || lookup.fpg[it.forma_pgto_id] || ""}>
                  <SelectValue placeholder="—" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">—</SelectItem>
                  {formas.map((f) => <SelectItem key={f.id} value={f.id}>{f.nome}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            {/* Lista dos pedidos internos — quando essa entrega tem múltiplos pedidos
                (qtd_pedidos > 1). Uma entrega no banco, mas o entregador precisa
                conferir cada número físico (caixas do mesmo cliente).
                Semântica nova (fev/2026): pedidos_multiplos guarda TODOS os N números
                — não só os extras. Exibimos como texto inline "Pedidos: #101, #102, #103". */}
            {(() => {
              const peds = Array.isArray(it.pedidos_multiplos) ? it.pedidos_multiplos.filter(Boolean) : [];
              if (peds.length < 2) return null;
              const withHash = (s) => {
                const t = String(s || "").trim();
                return t.startsWith("#") ? t : `#${t}`;
              };
              return (
                <div className="col-span-2 rounded-lg border border-blue-300 bg-blue-50 p-2" data-testid={`mobile-pedidos-list-${it.id}`}>
                  <div className="text-[10px] font-bold uppercase text-blue-800 mb-1">
                    Pedidos desta entrega ({peds.length})
                  </div>
                  <div className="mono text-sm text-blue-900 font-bold leading-tight" data-testid={`mobile-pedidos-text-${it.id}`}>
                    {peds.map((p, i) => (
                      <span
                        key={`ped-${i}-${p}`}
                        data-testid={`mobile-pedido-item-${it.id}-${i}`}
                        data-numero-pedido={p}
                      >
                        {withHash(p)}{i < peds.length - 1 ? ", " : ""}
                      </span>
                    ))}
                  </div>
                  <div className="text-[10px] text-blue-700 mt-1">Confira todas as caixas antes de entregar.</div>
                </div>
              );
            })()}
            {it.troco > 0 && <div className="col-span-2 bg-amber-50 border border-amber-200 rounded p-2 text-sm flex items-center justify-between gap-2"><span><b>Levar troco:</b> <span className="mono text-amber-800">{fmtBRL(it.troco)}</span></span>{it.troco_saida_confirmado && <span className="text-[11px] font-bold text-emerald-700" data-testid={`mobile-troco-saida-ok-${it.id}`}>✓ recebido do balcão</span>}</div>}
            {it.observacao && <div className="col-span-2 bg-sky-50 border border-sky-200 rounded p-2 text-sm text-sky-900" data-testid={`mobile-obs-admin-${it.id}`}><b>Obs. da loja:</b> {it.observacao}</div>}
            {it.observacao_entregador && <div className="col-span-2 text-xs text-slate-600"><b>Sua observação:</b> {it.observacao_entregador}</div>}
            {it.motivo && <div className="col-span-2 text-xs text-slate-600"><b>Motivo:</b> {it.motivo}</div>}
            {showConfProgress && (
              <div className="col-span-2 rounded-lg border-2 border-sky-300 bg-sky-50 p-2.5" data-testid={`mobile-conf-checklist-${it.id}`}>
                <div className="text-[11px] font-extrabold uppercase text-sky-900 tracking-wide mb-1.5 flex items-center gap-1.5">
                  <PackageCheck className="w-3.5 h-3.5" />
                  Checklist de pedidos ({confProgress.done}/{confProgress.total})
                </div>
                <ul className="space-y-1">
                  {confProgress.expected.map((pd) => {
                    const done = confProgress.confirmed.includes(pd);
                    return (
                      <li
                        key={pd}
                        className={`flex items-center gap-2 text-xs font-semibold ${done ? "text-emerald-800" : "text-slate-700"}`}
                        data-testid={`mobile-conf-item-${it.id}-${pd}`}
                        data-done={done ? "true" : "false"}
                      >
                        {done
                          ? <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0" />
                          : <span className="w-4 h-4 rounded-full border-2 border-slate-400 shrink-0"></span>}
                        <span className="mono">#{pd}</span>
                        {done && <span className="text-[10px] uppercase font-extrabold text-emerald-700">escaneado</span>}
                      </li>
                    );
                  })}
                </ul>
                <div className="text-[10px] text-sky-700 mt-1.5">
                  Escaneie o código de cada pedido — a entrega só sai como "Em Trânsito" quando todos forem conferidos.
                </div>
              </div>
            )}
          </div>
          <Button size="sm" variant="outline" onClick={() => onOpenChat(it)} data-testid={`btn-chat-${it.id}`}
            className={`w-full mb-2 h-12 relative overflow-visible ${unread ? "border-2 border-red-500 text-red-700 bg-red-50 hover:bg-red-100 chat-ring-pulse shadow-md" : "border-blue-300 text-blue-700 hover:bg-blue-50"}`}>
            <MessageSquare className={`w-5 h-5 mr-2 ${unread ? "chat-blink" : ""}`} />
            <span className="font-bold">
              {unread ? `${unread.count} nova${unread.count > 1 ? "s" : ""} mensagem${unread.count > 1 ? "s" : ""}` : "Falar com o atendente"}
            </span>
            {unread && (
              <span className="absolute -top-2 -right-2 bg-red-600 text-white text-xs font-extrabold rounded-full min-w-[24px] h-6 px-1.5 flex items-center justify-center shadow-lg ring-2 ring-white chat-blink" data-testid={`chat-badge-${it.id}`}>
                {unread.count > 9 ? "9+" : unread.count}
              </span>
            )}
          </Button>
          {it.status === "aguardando_conferencia" ? (
            <Button
              size="lg"
              className="w-full h-14 bg-violet-700 hover:bg-violet-800 text-white font-extrabold text-base shadow-md ring-2 ring-violet-300"
              onClick={() => onConfirmarRecebimento?.(it)}
              data-testid={`btn-confirmar-recebimento-${it.id}`}
            >
              <PackageCheck className="w-5 h-5 mr-2" />
              Confirmar Recebimento
            </Button>
          ) : it.status !== "concluida" && (
            <div className="grid grid-cols-3 gap-2">
              <Button size="sm"
                className={`h-12 ${busyStatus === "concluida" && busyPhase === "success" ? btnSuccessCls : "bg-emerald-600 hover:bg-emerald-700"}`}
                disabled={isBusy}
                onClick={() => onSetStatus(it, "concluida")}
                data-testid={`btn-concluir-${it.id}`}>
                {renderActionBtnContent("concluida", CheckCircle2, "Entreguei")}
              </Button>
              <Button size="sm" variant="outline"
                className={`h-12 ${busyStatus === "remarcar" && busyPhase === "success" ? "border-green-500 text-green-700 bg-green-50" : "border-rose-300 text-rose-600 hover:bg-rose-50"}`}
                disabled={isBusy}
                onClick={() => onSetStatus(it, "remarcar")}
                data-testid={`btn-remarcar-${it.id}`}>
                {renderActionBtnContent("remarcar", RotateCcw, "Remarcar")}
              </Button>
              <Button size="sm" variant="outline"
                className={`h-12 ${busyStatus === "cancelada" && busyPhase === "success" ? "border-green-500 text-green-700 bg-green-50" : "border-rose-400 text-rose-700 hover:bg-rose-50"}`}
                disabled={isBusy}
                onClick={() => onSetStatus(it, "cancelada")}
                data-testid={`btn-cancelar-${it.id}`}>
                {renderActionBtnContent("cancelada", Ban, "Cancelar")}
              </Button>
            </div>
          )}
        </>
      )}
      {isCollapsed && it.status === "aguardando_conferencia" && (
        <Button
          size="lg"
          className="w-full h-12 bg-violet-700 hover:bg-violet-800 text-white font-extrabold shadow-md ring-2 ring-violet-300"
          onClick={(e) => { e.stopPropagation(); onConfirmarRecebimento?.(it); }}
          data-testid={`btn-quick-confirmar-${it.id}`}
        >
          <PackageCheck className="w-4 h-4 mr-2" />
          Confirmar Recebimento
        </Button>
      )}
      {isCollapsed && it.status !== "concluida" && it.status !== "aguardando_conferencia" && (
        <Button size="sm"
          className={`w-full h-10 ${busyStatus === "concluida" && busyPhase === "success" ? btnSuccessCls : "bg-emerald-600 hover:bg-emerald-700"}`}
          disabled={isBusy}
          onClick={(e) => { e.stopPropagation(); onSetStatus(it, "concluida"); }}
          data-testid={`btn-quick-concluir-${it.id}`}>
          {busyStatus === "concluida" && busyPhase === "pending" ? (
            <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Enviando…</>
          ) : busyStatus === "concluida" && busyPhase === "success" ? (
            <><CheckCircle2 className="w-4 h-4 mr-2" />Enviado!</>
          ) : (
            <><CheckCircle2 className="w-4 h-4 mr-2" />Concluir esta entrega</>
          )}
        </Button>
      )}
    </div>
  );
}
